import type { EntityContentGroupDefinitions } from '@backstage/plugin-catalog-react/alpha';
import type {
  Icon,
  RouteWithSingleTab,
  RouteWithSubPath,
  SubRoute,
} from '@ipen-portal/plugin-common-react';

import {
  isRouteWithMultipleTabs,
  isRouteWithSubPath,
} from '@ipen-portal/plugin-common-react';
import { sortBy } from 'es-toolkit';

export type GroupedTabs = BaseItem & {
  tabs: NavEntry[];
};

type BaseItem = {
  active: boolean;
  icon?: Icon;
  title: string;
};

type NavEntry = BaseItem & {
  target: string;
};

const isExternalTarget = (subPath: string) =>
  subPath.startsWith('http') || subPath.startsWith('ipenportal://');

const getTabTarget = (
  tab: RouteWithSingleTab | RouteWithSubPath,
  subRoutePath: string,
) => {
  if (!isRouteWithSubPath(tab)) {
    return subRoutePath;
  }

  return isExternalTarget(tab.subPath)
    ? tab.subPath
    : `${subRoutePath}/${tab.subPath}`;
};

const isTabActive = (
  tab: RouteWithSingleTab | RouteWithSubPath,
  routeActive: boolean,
  selectedSubPath: string | undefined,
) =>
  (isRouteWithSubPath(tab)
    ? routeActive && tab.subPath === selectedSubPath
    : routeActive);

const processTab = (
  tab: RouteWithSingleTab | RouteWithSubPath,
  subRoutePath: string,
  routeActive: boolean,
  selectedSubPath: string | undefined,
  groupDefinitions: EntityContentGroupDefinitions | undefined,
  groups: Map<string, GroupedTabs>,
  acc: (GroupedTabs | NavEntry)[],
) => {
  const active = isTabActive(tab, routeActive, selectedSubPath);
  const navEntry = {
    active,
    icon: tab.icon,
    target: getTabTarget(tab, subRoutePath),
    title: tab.title,
  };

  if (tab.group) {
    let group = groups.get(tab.group);
    if (!group) {
      const groupDefinition = groupDefinitions?.[tab.group];
      if (groupDefinition) {
        group = {
          active,
          icon: groupDefinition.icon,
          tabs: [],
          title: groupDefinition.title,
        };
        groups.set(tab.group, group);
        acc.push(group);
      }
    }

    if (group) {
      if (active) {
        group.active = true;
      }
      group.tabs.push(navEntry);
      return;
    }
  }

  acc.push(navEntry);
};

export const processGroupedTabs = (
  subRoutes: readonly SubRoute[],
  selectedSubRoute: SubRoute | undefined,
  selectedSubPath: string | undefined,
  groupDefinitions: EntityContentGroupDefinitions | undefined,
  titleOrder: Record<string, number>,
) => {
  if (!subRoutes.length) {
    return [];
  }

  const groups = new Map<string, GroupedTabs>();

  const groupedTabs = subRoutes.reduce(
    (acc, subRoute) => {
      const routeActive = subRoute === selectedSubRoute;
      const tabs = isRouteWithMultipleTabs(subRoute)
        ? subRoute.tabs
        : [subRoute];

      for (const tab of tabs) {
        processTab(
          tab,
          subRoute.path,
          routeActive,
          selectedSubPath,
          groupDefinitions,
          groups,
          acc,
        );
      }
      return acc;
    },
    [] as (GroupedTabs | NavEntry)[],
  );

  return sortBy(groupedTabs, [
    tab => titleOrder[tab.title.toLowerCase()] ?? 9999,
  ]);
};
